import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../../utils/feedback';
import { formatUnit } from '../../../utils/units';
import {
  fetchStockValuation,
  saveStockValuation,
  StockValuation,
  StockValuationCommand,
} from './stockValuationApi';

const pln = (value: number | null) =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
      }).format(value);
interface Props {
  supplierOrderId: string;
  canEdit: boolean;
  onSaved: () => unknown | Promise<unknown>;
}

export default function StockValuationPanel({
  supplierOrderId,
  canEdit,
  onSaved,
}: Props) {
  const { t } = useTranslation('orders');
  const feedback = useFeedback();
  const [form] = Form.useForm();
  const [data, setData] = useState<StockValuation>();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const pending = useRef<StockValuationCommand | undefined>(undefined);
  const key = (name: string) => t(`supplierOrders.stockValuation.${name}`);
  const seed = useCallback((result: StockValuation) => {
    setData(result);
  }, []);
  useEffect(() => {
    if (data)
      form.setFieldsValue({
        reason: '',
        transports: data.transports.map((row) => ({
          transportId: row.id,
          percent: row.percent,
        })),
      });
  }, [data, form]);
  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      seed(await fetchStockValuation(supplierOrderId));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [supplierOrderId, seed]);
  useEffect(() => {
    void load();
  }, [load]);
  const save = async () => {
    if (!data) return;
    const values = await form.validateFields();
    const transports = (data?.transports || []).map((row, index) => ({
      transportId: row.id,
      percent: values.transports[index].percent,
    }));
    if (
      !pending.current ||
      pending.current.expectedVersion !== data.version ||
      pending.current.reason !== values.reason ||
      JSON.stringify(pending.current.transports) !== JSON.stringify(transports)
    ) {
      pending.current = {
        expectedVersion: data.version,
        requestKey: crypto.randomUUID(),
        reason: values.reason,
        transports,
      };
    }
    setLoading(true);
    try {
      seed(await saveStockValuation(supplierOrderId, pending.current));
      pending.current = undefined;
      feedback.success(key('saved'));
      await onSaved();
    } catch (error: any) {
      feedback.error(error?.response?.data?.message || key('saveFailed'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Card title={key('title')} loading={loading && !data}>
      {failed && (
        <Alert
          type="error"
          message={key('loadFailed')}
          action={<Button onClick={load}>{key('refresh')}</Button>}
        />
      )}
      {data && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type={data.valuationComplete ? 'success' : 'warning'}
            showIcon
            message={key(data.valuationComplete ? 'complete' : 'incomplete')}
            description={key('help')}
          />
          {data.issues.map((issue) => (
            <Alert key={issue} type="warning" message={issue} />
          ))}
          <Descriptions layout="vertical" column={{ xs: 1, sm: 2, lg: 3 }}>
            {(
              [
                'goodsPLN',
                'landedPLN',
                'freightPLN',
                'appliedPLN',
                'unreceivedExtraPLN',
                'remainingValuePLN',
              ] as const
            ).map((field) => (
              <Descriptions.Item key={field} label={key(field)}>
                <span style={{ whiteSpace: 'nowrap' }}>{pln(data[field])}</span>
              </Descriptions.Item>
            ))}
          </Descriptions>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={data.lines}
            scroll={{ x: 600 }}
            columns={[
              { title: key('product'), dataIndex: 'name' },
              {
                title: key('ordered'),
                render: (_, r) => `${r.ordered} ${formatUnit(r.unit)}`,
              },
              {
                title: key('received'),
                render: (_, r) => `${r.received} ${formatUnit(r.unit)}`,
              },
              {
                title: key('remaining'),
                render: (_, r) => `${r.remaining} ${formatUnit(r.unit)}`,
              },
            ]}
          />
          <Form
            form={form}
            layout="vertical"
            disabled={!canEdit || loading || failed}
            onFinish={() => void save()}
          >
            <Typography.Paragraph>{key('allocationHelp')}</Typography.Paragraph>
            {data.transports.map((row, index) => (
              <Space key={row.id} align="start" wrap>
                <Typography.Text>
                  {row.label}:{' '}
                  {row.originalAmount == null
                    ? '—'
                    : row.originalAmount.toLocaleString('pl-PL')}{' '}
                  {row.currency}
                </Typography.Text>
                <Form.Item
                  name={['transports', index, 'percent']}
                  label={key('share')}
                  rules={[
                    { required: true, message: key('shareRequired') },
                    { type: 'number', min: 0, max: 100 - row.otherPercent },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100 - row.otherPercent}
                    precision={4}
                    addonAfter="%"
                    disabled={row.hasCustomerOrders || !canEdit || loading}
                  />
                </Form.Item>
                <Typography.Text type="secondary">
                  {key('otherShares')}: {row.otherPercent}%
                </Typography.Text>
                <Typography.Text>
                  {key('allocatedFreight')}:{' '}
                  {row.amountPLN == null ? '—' : pln(row.amountPLN)}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {key('unallocatedFreight')}:{' '}
                  {row.unallocatedPLN == null ? '—' : pln(row.unallocatedPLN)}
                </Typography.Text>
              </Space>
            ))}
            <Form.Item
              name="reason"
              label={key('reason')}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: key('reasonRequired'),
                },
                { max: 1000 },
              ]}
            >
              <Input.TextArea rows={2} maxLength={1000} />
            </Form.Item>
            <Space wrap>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={
                  !canEdit || data.transports.some((t) => t.hasCustomerOrders)
                }
              >
                {key('apply')}
              </Button>
              <Button onClick={load}>{key('refresh')}</Button>
            </Space>
          </Form>
          <Typography.Text type="secondary">
            {key('lateCostHelp')}
          </Typography.Text>
          <Table
            size="small"
            rowKey="id"
            dataSource={data.history}
            pagination={{ pageSize: 5 }}
            columns={[
              {
                title: key('date'),
                dataIndex: 'createdAt',
                render: (value) => new Date(value).toLocaleString('pl-PL'),
              },
              { title: key('author'), dataIndex: 'createdBy' },
              { title: key('reason'), dataIndex: 'reason' },
            ]}
          />
          <Tag>{key('allocationBasis')}</Tag>
        </Space>
      )}
    </Card>
  );
}
